import {
  faCalendarAlt,
  faCamera,
  faChartArea,
  faCheckCircle,
  faFlask,
  faHome,
  faList,
  faMapMarkedAlt,
  faProjectDiagram,
  faSitemap,
  faVideo,
  faVirus,
} from "@fortawesome/free-solid-svg-icons";
import { percent } from "csx";
import { Vertical } from "gls";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { classes } from "typestyle";
import { Dashboards } from "../Dashboards";
import { defaultSearchParams } from "../Sidebar";
import { SidebarItem } from "../SidebarItem";
import { SidebarItemGroup } from "../SidebarItemGroup";
import { useIsStageOrDev } from "@/shared/hooks/useIsStageOrDev";

export const SideBarMainMenu: FC<{
  isCollapsed: boolean;
}> = ({ isCollapsed }) => {
  const { t } = useTranslation();
  const isStageOrDev = useIsStageOrDev();
  const isElectron = !!window?.electronAPI;

  return (
    <Vertical
      sizing="stretch"
      width={percent(100)}
      className={classes(
        "overflow-y-auto sm:pt-0 md:pt-20",
        !isCollapsed && "pr-2",
      )}
    >
      {isStageOrDev && (
        <SidebarItemGroup label="Shell" isCollapsed={isCollapsed}>
          <SidebarItem
            isCollapsed={isCollapsed}
            icon={faHome}
            text={t("Home")}
            collapsedText={t("Home")}
            navigateTo={"/welcome"}
            data-testid="shell-home-link"
          />
          <SidebarItem
            isCollapsed={isCollapsed}
            icon={faFlask}
            text="Test MFE"
            collapsedText="Test"
            navigateTo={"/test-mfe"}
            data-testid="shell-test-mfe-link"
          />
        </SidebarItemGroup>
      )}
      <SidebarItemGroup label="Track" isCollapsed={isCollapsed}>
        <SidebarItem
          isCollapsed={isCollapsed}
          icon={faMapMarkedAlt}
          text={t("Map")}
          collapsedText={t("Map")}
          navigateTo={"/tide/map"}
          navigateSearch={defaultSearchParams}
          permissionName={"MAP_PAGE"}
          data-testid="sidebar-item-map"
        />
        <SidebarItem
          isCollapsed={isCollapsed}
          icon={faCheckCircle}
          text={t("Goals")}
          collapsedText={t("Goals")}
          navigateTo={"/tide/goals"}
          navigateSearch={defaultSearchParams}
          permissionName={"GOALS_PAGE"}
          data-testid="sidebar-item-goals"
        />

        <SidebarItem
          isCollapsed={isCollapsed}
          icon={faCalendarAlt}
          text={t("Calendar")}
          collapsedText={t("Calendar")}
          navigateTo={"/tide/calendar"}
          navigateSearch={defaultSearchParams}
          permissionName={"CALENDAR_PAGE"}
          data-testid="sidebar-item-calendar"
        />
        <SidebarItem
          isCollapsed={isCollapsed}
          icon={faVirus}
          text={"Biolife"}
          collapsedText={"Biolife"}
          navigateTo={"/tide/biolife"}
          navigateSearch={defaultSearchParams}
          permissionName={"ALGAE_DASHBOARD"}
          data-testid="sidebar-item-biolife"
        />
      </SidebarItemGroup>
      <SidebarItemGroup label="Explore" isCollapsed={isCollapsed}>
        <Dashboards isCollapsed={isCollapsed} />
        <SidebarItem
          key="analytics"
          isCollapsed={isCollapsed}
          icon={faChartArea}
          text={t("Analytics")}
          collapsedText={t("Data")}
          navigateTo={"/tide/analytics"}
          navigateSearch={defaultSearchParams}
          permissionName={"DATA_PAGE"}
          data-testid="sidebar-item-analytics"
        />
        <SidebarItem
          isCollapsed={isCollapsed}
          svgIcon="fish"
          text={t("Feeding Analytics")}
          collapsedText={t("Feeding Analytics")}
          navigateTo={"/akva-analytics"}
          navigateSearch={defaultSearchParams}
          permissionName={"ACVA_CONTROL_ANALYTICS"}
          data-testid="akva-feeding-analytics-link"
        />
      </SidebarItemGroup>

      <SidebarItemGroup label="Act" isCollapsed={isCollapsed}>
        {isElectron && (
          <>
            <SidebarItem
              isCollapsed={isCollapsed}
              icon={faVideo}
              text={t("Video")}
              collapsedText={t("Video")}
              navigateTo={"/marlin-v2/video"}
              permissionName={"VIDEO_PAGE"}
              data-testid="video-navigation-link"
            />
            <SidebarItem
              isCollapsed={isCollapsed}
              icon={faList}
              text={t("Feeding")}
              collapsedText={t("Feeding")}
              navigateTo={"/marlin-v2/feeding"}
              permissionName={"FEEDING_PAGE"}
              data-testid="feeding-navigation-link"
            />
          </>
        )}
        <SidebarItem
          isCollapsed={isCollapsed}
          icon={faProjectDiagram}
          text={t("Feed Automation")}
          collapsedText={t("Feed")}
          navigateTo={"/tide/feeding-strategies"}
          navigateSearch={defaultSearchParams}
          permissionName={"FEED_PAGE"}
          data-testid="sidebar-item-feeding-strategies"
        />
        <SidebarItem
          isCollapsed={isCollapsed}
          icon={faSitemap}
          text={"Automation"}
          collapsedText={"Automation"}
          navigateTo={"/tide/automation-form/select"}
          navigateSearch={defaultSearchParams}
          permissionName={"AUTOMATION_FORMS"}
          data-testid="sidebar-item-automation"
        />
        <SidebarItem
          isCollapsed={isCollapsed}
          icon={faCamera}
          text={t("Camera Automation")}
          collapsedText={t("Camera")}
          navigateTo={"/tide/camera-automation"}
          navigateSearch={defaultSearchParams}
          permissionName={"CAMERA_PAGE"}
          data-testid="sidebar-item-camera-automation"
        />
      </SidebarItemGroup>
    </Vertical>
  );
};
